/**
 * Membership Index Service
 *
 * Maintains a denormalized index at `users/{uid}/memberships/{teamId}`
 * to enable fast lookup of a user's teams without scanning all teams.
 *
 * The source of truth remains `teams/{teamId}/members/{uid}`.
 * This index is updated alongside every membership write.
 */

import {
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  collectionGroup,
} from 'firebase/firestore';
import { db, auth } from '@/lib/firebase';
import { logger } from '@/lib/logger';

/**
 * Membership index document structure
 * Path: users/{uid}/memberships/{teamId}
 */
export interface MembershipIndexDoc {
  teamId: string;
  teamRole: 'owner' | 'admin' | 'manager' | 'member' | 'viewer';
  active: boolean;
  joinedAt: ReturnType<typeof serverTimestamp> | Date | null;
  teamName?: string;
  updatedAt: ReturnType<typeof serverTimestamp>;
}

/**
 * Input for upserting a membership index entry
 */
export interface UpsertMembershipIndexInput {
  uid: string;
  teamId: string;
  teamRole: 'owner' | 'admin' | 'manager' | 'member' | 'viewer';
  active: boolean;
  joinedAt?: ReturnType<typeof serverTimestamp> | Date | null;
  teamName?: string;
}

/**
 * Upsert a membership index entry
 *
 * Call this whenever a membership is created or updated at
 * `teams/{teamId}/members/{uid}`.
 *
 * @param input - Membership data to index
 */
export async function upsertMembershipIndex(input: UpsertMembershipIndexInput): Promise<void> {
  const { uid, teamId, teamRole, active, joinedAt, teamName } = input;

  const indexRef = doc(db, 'users', uid, 'memberships', teamId);

  const indexDoc: MembershipIndexDoc = {
    teamId,
    teamRole,
    active,
    joinedAt: joinedAt ?? null,
    updatedAt: serverTimestamp(),
  };

  if (teamName) {
    indexDoc.teamName = teamName;
  }

  await setDoc(indexRef, indexDoc, { merge: true });

  logger.info('[MembershipIndex] Upserted index:', { uid, teamId, teamRole, active });
}

/**
 * Delete a membership index entry
 *
 * Call this when a membership is permanently deleted (not just deactivated).
 * For deactivation, use upsertMembershipIndex with active: false.
 *
 * @param uid - User ID
 * @param teamId - Team ID
 */
export async function deleteMembershipIndex(uid: string, teamId: string): Promise<void> {
  const indexRef = doc(db, 'users', uid, 'memberships', teamId);
  await deleteDoc(indexRef);

  logger.info('[MembershipIndex] Deleted index:', { uid, teamId });
}

/**
 * Get all active team memberships for a user
 *
 * This is the PRIMARY lookup method - replaces scanning all teams.
 * Reads from users/{uid}/memberships index (written by CF after bootstrap).
 *
 * @param uid - User ID
 * @returns Array of membership index docs where active !== false
 */
export async function getUserActiveMemberships(uid: string): Promise<MembershipIndexDoc[]> {
  // INVESTIGATION: Log exact query shape for rules compatibility check
  logger.info('[MembershipIndex] INVESTIGATION - Query shape', {
    uid,
    path: `users/${uid}/memberships`,
    rulesPath: 'match /users/{uid}/memberships/{teamId}',
    rulesCondition: 'signedIn() && request.auth.uid == uid',
    expectedToMatch: auth.currentUser?.uid === uid,
  });

  const membershipsRef = collection(db, 'users', uid, 'memberships');

  try {
    const snapshot = await getDocs(membershipsRef);

    logger.info('[MembershipIndex] Query succeeded', {
      uid,
      docCount: snapshot.docs.length,
      docs: snapshot.docs.map((d) => ({ teamId: d.id, data: d.data() })),
    });

    // Treat missing active field as active (backward compat with older index docs)
    return snapshot.docs
      .map((doc) => doc.data() as MembershipIndexDoc)
      .filter((m) => m.active !== false);
  } catch (error) {
    logger.error('[MembershipIndex] INVESTIGATION - Query failed', {
      uid,
      authUid: auth.currentUser?.uid,
      authReady: !!auth.currentUser,
      error: error instanceof Error ? error.message : String(error),
      errorCode: (error as any)?.code,
    });
    throw error;
  }
}

/**
 * Get all team memberships for a user (including inactive)
 *
 * @param uid - User ID
 * @returns Array of all membership index docs
 */
export async function getUserAllMemberships(uid: string): Promise<MembershipIndexDoc[]> {
  const membershipsRef = collection(db, 'users', uid, 'memberships');
  const snapshot = await getDocs(membershipsRef);

  return snapshot.docs.map((doc) => doc.data() as MembershipIndexDoc);
}

/**
 * Find user's first active team ID from the index
 *
 * This replaces the old findUserTeamMembership that scanned all teams.
 *
 * @param uid - User ID
 * @returns Team ID if found, null otherwise
 */
export async function findUserTeamFromIndex(uid: string): Promise<string | null> {
  const memberships = await getUserActiveMemberships(uid);

  if (memberships.length === 0) {
    return null;
  }

  // Return the first active team (could be enhanced to prefer owner role)
  const firstMembership = memberships[0];
  return firstMembership ? firstMembership.teamId : null;
}

/**
 * Fallback: Find user's team membership via collectionGroup query
 *
 * Used as a temporary fallback during transition period for users
 * who don't have index docs yet. If found, creates the missing index.
 *
 * Query shape: collectionGroup('members') where uid == auth.uid
 * NOTE: We do NOT filter by active==true here — that would exclude docs
 * where the active field is absent (backward compat) and would prevent
 * self-healing for disabled members. Active filtering is done in code.
 *
 * @param uid - User ID
 * @returns Team ID if found (including disabled memberships), null if none exist
 */
export async function findUserTeamFallback(uid: string): Promise<string | null> {
  try {
    // Uid-only filter — no active==true filter (see note above)
    const membersQuery = query(collectionGroup(db, 'members'), where('uid', '==', uid));

    const snapshot = await getDocs(membersQuery);

    if (snapshot.empty) {
      // Normal during first-run bootstrap — no membership exists yet
      logger.info('[MembershipIndex] Fallback: no membership docs found (expected for new users)', {
        uid,
      });
      return null;
    }

    // Prefer active memberships; fall back to any membership for self-heal
    const activeDocs = snapshot.docs.filter((d) => d.data().active !== false);
    const memberDoc = activeDocs[0] ?? snapshot.docs[0];
    if (!memberDoc) return null;

    const memberData = memberDoc.data();

    // Extract teamId from path: teams/{teamId}/members/{uid}
    const pathParts = memberDoc.ref.path.split('/');
    const teamId = pathParts[1];

    if (!teamId) {
      logger.warn('[MembershipIndex] Could not extract teamId from path:', memberDoc.ref.path);
      return null;
    }

    // Self-heal: write the index so future lookups hit the fast path.
    // Rules now allow client writes to users/{uid}/memberships/{teamId}.
    try {
      const indexRef = doc(db, 'users', uid, 'memberships', teamId);
      await setDoc(
        indexRef,
        {
          teamId,
          teamRole: (memberData.teamRole ?? memberData.role ?? 'owner') as
            | 'owner'
            | 'admin'
            | 'manager'
            | 'member'
            | 'viewer',
          active: memberData.active !== false,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      logger.info('[MembershipIndex] Self-healed index from fallback', { uid, teamId });
    } catch (healErr) {
      logger.warn('[MembershipIndex] Self-heal write failed (non-critical)', {
        uid,
        teamId,
        healErr,
      });
    }

    logger.info('[MembershipIndex] Fallback found membership doc', {
      uid,
      teamId,
      active: memberData.active,
    });
    return teamId;
  } catch (error) {
    const isPermissionDenied =
      error instanceof Error && error.message.includes('permission-denied');
    const isSignedIn = !!auth.currentUser;

    if (isPermissionDenied && !isSignedIn) {
      logger.warn(
        '[MembershipIndex] Fallback query denied — auth not ready yet (user not signed in)',
        { uid }
      );
    } else if (isPermissionDenied) {
      logger.error('[MembershipIndex] Fallback query permission-denied despite signed-in user', {
        uid,
        authUid: auth.currentUser?.uid,
        query: `collectionGroup(members) where uid==${uid}`,
      });
    } else {
      logger.error('[MembershipIndex] Fallback query failed:', error);
    }
    // Always return null — let bootstrap proceed to team creation
    return null;
  }
}

/**
 * Find user's team membership with index + fallback
 *
 * This is the main entry point that replaces the old findUserTeamMembership.
 *
 * 1. Primary: Check users/{uid}/memberships index
 * 2. Fallback: If empty, run collectionGroup query and self-heal
 * 3. If still none: Return null (NO_TEAM)
 *
 * @param uid - User ID
 * @returns Team ID if found, null otherwise
 */
export async function findUserTeamMembership(uid: string): Promise<string | null> {
  // Step 1: Try the index first (fast path)
  const teamIdFromIndex = await findUserTeamFromIndex(uid);
  if (teamIdFromIndex) {
    logger.info('[MembershipIndex] Found team via index:', { uid, teamId: teamIdFromIndex });
    return teamIdFromIndex;
  }

  // Step 2: Fallback to collectionGroup query (for transition period)
  logger.info('[MembershipIndex] Index empty, trying fallback query:', { uid });
  const teamIdFromFallback = await findUserTeamFallback(uid);
  if (teamIdFromFallback) {
    return teamIdFromFallback;
  }

  // Step 3: No team found
  logger.info('[MembershipIndex] No team found for user:', { uid });
  return null;
}
